// src/components/PhotoWhiteBgGate.jsx
import React, { useRef, useState, useEffect, useCallback } from "react";
import imageCompression from "browser-image-compression";
import {
  initVision,
  getFaceLandmarker,
  getSegmenter,
} from "../../utils/vision";

async function toImageBitmap(fileOrBlob) {
  try {
    return await createImageBitmap(fileOrBlob);
  } catch {
    const url = URL.createObjectURL(fileOrBlob);
    const img = await new Promise((res, rej) => {
      const el = new Image();
      el.onload = () => res(el);
      el.onerror = rej;
      el.src = url;
    });
    const c = document.createElement("canvas");
    c.width = img.width;
    c.height = img.height;
    c.getContext("2d").drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    return await createImageBitmap(c);
  }
}

function computeSmileRatio(landmarks = []) {
  const needed = [61, 291, 234, 454];
  for (const idx of needed) if (!landmarks[idx]) return 0;
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const mouthWidth = dist(landmarks[61], landmarks[291]);
  const faceWidth = dist(landmarks[234], landmarks[454]) || 1;
  return mouthWidth / faceWidth;
}

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return { h, s, l };
}

// Lee máscara y detecta si es probabilística (0..1) o de índices (0..N)
function readMask(categoryMask) {
  let w,
    h,
    arr,
    isFloat,
    isIndexMask = false;
  if (typeof categoryMask?.getAsFloat32Array === "function") {
    arr = categoryMask.getAsFloat32Array();
    w = categoryMask.width;
    h = categoryMask.height;
    isFloat = true;
  } else if (categoryMask?.data && typeof categoryMask.width === "number") {
    arr = categoryMask.data;
    w = categoryMask.width;
    h = categoryMask.height;
    isFloat = false;
  } else throw new Error("Máscara inválida");
  // heurística: valores >1 ⇒ índices
  const total = w * h;
  for (let i = 0; i < Math.min(total, 20000); i += 97) {
    const v = isFloat ? arr[i] : arr[i] / 255;
    if (v > 1.01) {
      isIndexMask = true;
      break;
    }
  }
  return { w, h, arr, isFloat, isIndexMask };
}

//Valida que el FONDO sea suficientemente "blanco".
function isBackgroundWhite(srcImg, categoryMask, opts = {}) {
  const {
    threshold = 0.5,
    sampleStride = 4,
    minWhitePct = 0.7,
    maxS = 0.22,
    minL = 0.78,
    maxRgbDelta = 36,
    minRgb = 195,
    borderCropPx = 12,
    ambiguousBand = 0.08,
    minBgSamples = 800,
    fallbackBorderPct = 0.07,
    skipRadius = 3,
  } = opts;

  const { w, h, arr, isFloat, isIndexMask } = readMask(categoryMask);

  // Render “cover” al tamaño de la máscara
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  const sw = srcImg.width,
    sh = srcImg.height;
  const scale = Math.max(w / sw, h / sh);
  const dw = Math.round(sw * scale),
    dh = Math.round(sh * scale);
  const dx = Math.round((w - dw) / 2),
    dy = Math.round((h - dh) / 2);
  ctx.drawImage(srcImg, dx, dy, dw, dh);
  const pixels = ctx.getImageData(0, 0, w, h).data;

  const hiT = Math.min(1, threshold + ambiguousBand);
  const loT = Math.max(0, threshold - ambiguousBand);

  const isBg_A = (i) => {
    // no invertido (A)
    if (isIndexMask) {
      const raw = isFloat ? arr[i] : arr[i];
      return raw === 0; // 0 = fondo
    } else {
      const v = isFloat ? arr[i] : arr[i] / 255;
      return v <= loT; // prob fondo
    }
  };
  const isBg_B = (i) => {
    // invertido (B)
    if (isIndexMask) {
      const raw = isFloat ? arr[i] : arr[i];
      return raw !== 0; // >0 = fondo
    } else {
      const v = isFloat ? arr[i] : arr[i] / 255;
      return v >= hiT; // prob fondo invertido
    }
  };

  const personNear = (i, which) => {
    if (skipRadius <= 0) return false;
    const cx = i % w,
      cy = (i / w) | 0,
      r = skipRadius;
    const x0 = Math.max(0, cx - r),
      x1 = Math.min(w - 1, cx + r);
    const y0 = Math.max(0, cy - r),
      y1 = Math.min(h - 1, cy + r);
    for (let y = y0; y <= y1; y++) {
      const base = y * w;
      for (let x = x0; x <= x1; x++) {
        const j = base + x;
        const bg = which === "A" ? isBg_A(j) : isBg_B(j);
        if (!bg) return true; // hay persona cerca
      }
    }
    return false;
  };

  const testWhite = (p) => {
    const r = pixels[p],
      g = pixels[p + 1],
      b = pixels[p + 2];
    const { s, l } = rgbToHsl(r, g, b);

    // 1) brillo suficiente en los tres canales
    if (r < minRgb || g < minRgb || b < minRgb) return false;

    // 2) casi neutro: no permitir dominantes
    const dRG = Math.abs(r - g);
    const dGB = Math.abs(g - b);
    const dRB = Math.abs(r - b);
    const maxDelta = Math.max(dRG, dGB, dRB);
    if (maxDelta > 22) return false; // antes 36 → baja a 22 (≈8–9% de 255)
    // 3) si aún hay leve dominante azul/verde/rojo, corta por “sesgo azul”
    const blueBias = b - Math.max(r, g);
    if (blueBias > 12) return false; // >12 niveles sobre los demás ⇒ no es blanco
    // 4) HSL: blanco ≈ poca saturación y luminancia alta (un poco más estricto)
    if (s > Math.min(maxS, 0.18)) return false; // limita saturación (<= 0.18)
    if (l < Math.max(minL, 0.82)) return false; // exige luminancia algo mayor (>= 0.82)

    return true;
  };

  let bgA = 0,
    whiteA = 0,
    bgB = 0,
    whiteB = 0;

  for (let y = borderCropPx; y < h - borderCropPx; y += sampleStride) {
    const base = y * w;
    for (let x = borderCropPx; x < w - borderCropPx; x += sampleStride) {
      const i = base + x;
      if (isBg_A(i) && !(skipRadius > 0 && personNear(i, "A"))) {
        bgA++;
        if (testWhite(i * 4)) whiteA++;
      }
      if (isBg_B(i) && !(skipRadius > 0 && personNear(i, "B"))) {
        bgB++;
        if (testWhite(i * 4)) whiteB++;
      }
    }
  }

  const pctA = bgA ? whiteA / bgA : 0;
  const pctB = bgB ? whiteB / bgB : 0;

  let chosen = "A",
    pct = pctA,
    bgCount = bgA,
    whiteCount = whiteA;
  if (bgB >= Math.max(minBgSamples, bgA) && pctB > pctA) {
    chosen = "B";
    pct = pctB;
    bgCount = bgB;
    whiteCount = whiteB;
  } else if (bgA < minBgSamples && bgB >= minBgSamples) {
    chosen = "B";
    pct = pctB;
    bgCount = bgB;
    whiteCount = whiteB;
  }

  if (bgCount >= minBgSamples) {
    console.log(
      `[BG by mask ${chosen}] samples=${bgCount}, white=${(pct * 100).toFixed(
        1
      )}%`
    );
    if (pct >= minWhitePct) return true;
  } else {
    // Fallback: tiras de borde
    const pad = Math.floor(Math.min(w, h) * fallbackBorderPct);
    let fbA = 0,
      fbAw = 0,
      fbB = 0,
      fbBw = 0;

    const testPixel = (x, y) => {
      const i = y * w + x,
        p = i * 4;
      const wht = testWhite(p);
      if (isBg_A(i)) {
        fbA++;
        if (wht) fbAw++;
      }
      if (isBg_B(i)) {
        fbB++;
        if (wht) fbBw++;
      }
    };

    for (let y = borderCropPx; y < borderCropPx + pad; y += sampleStride)
      for (let x = borderCropPx; x < w - borderCropPx; x += sampleStride)
        testPixel(x, y);
    for (
      let y = h - borderCropPx - pad;
      y < h - borderCropPx;
      y += sampleStride
    )
      for (let x = borderCropPx; x < w - borderCropPx; x += sampleStride)
        testPixel(x, y);
    for (let x = borderCropPx; x < borderCropPx + pad; x += sampleStride)
      for (let y = borderCropPx; y < h - borderCropPx; y += sampleStride)
        testPixel(x, y);
    for (
      let x = w - borderCropPx - pad;
      x < w - borderCropPx;
      x += sampleStride
    )
      for (let y = borderCropPx; y < h - borderCropPx; y += sampleStride)
        testPixel(x, y);

    const pA = fbA ? fbAw / fbA : 0,
      pB = fbB ? fbBw / fbB : 0;
    const pick =
      pB > pA ? { tag: "B", p: pB, n: fbB } : { tag: "A", p: pA, n: fbA };
    console.log(
      `[BG fallback ${pick.tag}] samples=${pick.n}, white=${(
        pick.p * 100
      ).toFixed(1)}%`
    );
    return pick.p >= minWhitePct;
  }

  return false;
}

/**
 * <PhotoWhiteBgGate>
 * Render prop que te da openPicker() y maneja validación:
 *  - 1 rostro
 *  - sonrisa moderada
 *  - fondo blanco (robusto)
 */
export default function PhotoWhiteBgGate({
  accept = ".jpg,.jpeg,.png",
  maxMB = 5,
  onPass,
  onFail,
  children,
  bgOptions = {
    threshold: 0.5,
    sampleStride: 4,
    minWhitePct: 0.7,
    maxS: 0.22,
    minL: 0.78,
    maxRgbDelta: 36,
    minRgb: 195,
    borderCropPx: 12,
    ambiguousBand: 0.08,
    minBgSamples: 800,
    fallbackBorderPct: 0.07,
    skipRadius: 3,
  },
  smileLimits = { maxSmileRatio: 0.45, maxSmileScore: 0.6 },
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    initVision().catch((e) => console.error("initVision error:", e));
  }, []);

  const openPicker = useCallback(() => {
    setError(null);
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = ""; // permite re-seleccionar el mismo archivo
      if (!file) return;

      if (file.size > maxMB * 1024 * 1024) {
        const err = new Error(`El archivo supera ${maxMB}MB.`);
        setError(err.message);
        onFail?.(err);
        return;
      }

      setBusy(true);
      setError(null);

      try {
        // Precompresión ligera para acelerar IA
        const pre = await imageCompression(file, {
          maxWidthOrHeight: 1600,
          maxSizeMB: 0.6,
          initialQuality: 0.9,
          useWebWorker: true,
        });

        await initVision();
        const fl = getFaceLandmarker();
        const seg = getSegmenter();

        const bitmap = await toImageBitmap(pre);
        const faceRes = await fl.detect(bitmap);

        const faces = Array.isArray(faceRes?.faceLandmarks)
          ? faceRes.faceLandmarks
          : [];
        const blends = Array.isArray(faceRes?.faceBlendshapes)
          ? faceRes.faceBlendshapes
          : [];

        if (faces.length !== 1)
          throw new Error("La foto debe contener exactamente un rostro.");

        // Sonrisa: dos métricas
        const ratio = computeSmileRatio(faces[0]);
        let smileScore = 0;
        try {
          const cats = blends?.[0]?.categories;
          if (Array.isArray(cats)) {
            const find = (n) =>
              cats.find((c) => c.categoryName === n)?.score ?? 0;
            smileScore = Math.min(
              1,
              (find("mouthSmileLeft") + find("mouthSmileRight")) / 2
            );
          }
        } catch {
          /* ignore */
        }
        const { maxSmileRatio, maxSmileScore } = smileLimits;
        if (ratio > maxSmileRatio && smileScore > maxSmileScore) {
          throw new Error(
            "Expresión muy sonriente. Mantén una expresión más neutral."
          );
        }

        // Fondo blanco
        const segRes = await seg.segment(bitmap);
        const mask = segRes?.categoryMask;
        if (!mask) throw new Error("No se pudo analizar el fondo de la foto.");

        // Pasar a <img/> para muestrear
        const tmp = document.createElement("canvas");
        tmp.width = bitmap.width;
        tmp.height = bitmap.height;
        tmp.getContext("2d").drawImage(bitmap, 0, 0);
        const img = new Image();
        img.src = tmp.toDataURL();
        await new Promise((r) => (img.onload = r));

        const ok = isBackgroundWhite(img, mask, bgOptions);
        if (!ok) {
          throw new Error("El fondo debe ser blanco y relativamente uniforme.");
        }

        onPass?.(file);
      } catch (err) {
        setError(err.message || "Error al validar la foto.");
        onFail?.(err);
      } finally {
        setBusy(false);
      }
    },
    [bgOptions, smileLimits, maxMB, onPass, onFail]
  );

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={handleChange}
      />
      {typeof children === "function"
        ? children({ openPicker, busy, error })
        : null}
    </>
  );
}
